import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, Inject, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { escape, orderBy } from 'lodash';
import { distinctUntilChanged, filter, interval, map, skip, startWith } from 'rxjs';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { UtcToUserFormattedPipe } from '../../../../core/pipes/utc-to-user-formatted.pipe';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { IDropdownOption, MatDropdown } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../shared/components/mat-dropdown/mat-dropdown.interface';
import { SegmentedButtonComponent } from '../../../../shared/components/segmented-button/segmented-button.component';
import { ISegmentedButtonOption } from '../../../../shared/components/segmented-button/segmented-button.interface';
import { TextAvatarComponent } from '../../../../shared/components/text-avatar/text-avatar.component';
import { DateUtilitiesService } from '../../../../utilities/date-utilities.service';
import {
  ECheckInDestinationType,
  ECheckInUserSource,
  ICheckIn,
  ICheckInDestination,
  ICheckInUserCard,
  IWorkInstructionCheckInDialogData,
  IWorkInstructionCheckInRow,
  IWorkInstructionCheckInStep,
  IWorkInstructionCheckInTarget,
} from './work-instruction-check-in.interface';
import { WorkInstructionCheckInStore } from './work-instruction-check-in.store';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_DAY = 86400;
const DURATION_REFRESH_MS = 30_000;

@Component({
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatProgressBar,
    MatDropdown,
    SegmentedButtonComponent,
    TextAvatarComponent,
    UtcToUserFormattedPipe,
  ],
  providers: [WorkInstructionCheckInStore],
  selector: 'app-work-instruction-check-in',
  styleUrl: './work-instruction-check-in.component.scss',
  templateUrl: './work-instruction-check-in.component.html',
})
export class WorkInstructionCheckInComponent implements OnInit {
  /** Several work instructions → "Check-ins" view. */
  public readonly isMultiple: boolean = this.data.workInstructions.length > 1;
  public readonly title: string = this.isMultiple
    ? this.translate.instant('workInstruction.checkIn.checkInsTitle')
    : `${this.translate.instant('button.checkIn')} · ${this.data.workInstructions[0]?.name ?? ''}`;
  private readonly workInstructionNames = new Map<number, string>(
    this.data.workInstructions.map((workInstruction: IWorkInstructionCheckInTarget) => [
      workInstruction.itemGuideAssignmentId,
      workInstruction.name,
    ]),
  );
  private readonly stepsByAssignmentId = new Map<number, IWorkInstructionCheckInStep[]>(
    this.data.workInstructions.map((workInstruction: IWorkInstructionCheckInTarget) => [
      workInstruction.itemGuideAssignmentId,
      workInstruction.steps ?? [],
    ]),
  );
  public readonly workInstructionOptions: IDropdownOption[] = this.data.workInstructions.map(
    (workInstruction: IWorkInstructionCheckInTarget) => ({
      id: workInstruction.itemGuideAssignmentId,
      name: workInstruction.name,
    }),
  );
  // Validators.required also makes mat-dropdown show the mandatory "*" on its label.
  public readonly workInstructionControl = new FormControl<IDropdownOption[] | null>(
    this.getInitialWorkInstructionOptions(),
    Validators.required,
  );
  public readonly workInstructionDropdownConfiguration: IDropdownSettings = {
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.workInstruction'),
  };
  /** Optional: a step → check in to the step; empty → check in to the work instruction. */
  public readonly stepControl = new FormControl<IDropdownOption[] | null>(this.getInitialStepOptions());
  public readonly stepDropdownConfiguration: IDropdownSettings = {
    // Optional field, never has an error: don't reserve the error line under it (the hint sits there instead).
    hasMatError: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('workInstruction.checkIn.step'),
  };
  /** The new check-in form takes room the check-in list needs: it is shown only on request. */
  public readonly isNewCheckInOpen: WritableSignal<boolean> = signal(false);
  /** Checked-out check-ins are listed under the ongoing ones of their work instruction only on request. */
  public readonly isCheckedOutShown: WritableSignal<boolean> = signal(false);
  private areUserListsRequested = false;
  /** Set by a check-in attempt with missing fields: lets the dropdowns show their "required" errors. */
  public isCheckInSubmitted = false;
  /** The picked user card, as a one-element array like the other pickers. */
  public readonly userControl = new FormControl<IDropdownOption[] | null>([], Validators.required);
  private readonly selectedUserId: Signal<number | undefined> = toSignal(
    this.userControl.valueChanges.pipe(
      startWith(this.userControl.value),
      map((value: IDropdownOption[] | null) => value?.[0]?.id),
    ),
    { initialValue: undefined },
  );
  public readonly userSourceOptions: ISegmentedButtonOption<ECheckInUserSource>[] = [
    {
      icon: 'factory',
      labelKey: 'workInstruction.checkIn.userSource.oeeLine',
      value: ECheckInUserSource.oeeLine,
    },
    {
      icon: 'group',
      labelKey: 'workInstruction.checkIn.userSource.boardMembers',
      value: ECheckInUserSource.boardMembers,
    },
  ];
  private readonly selectedUserSource: WritableSignal<ECheckInUserSource> = signal(ECheckInUserSource.oeeLine);
  /** The source choice is offered only when the item is linked to an OEE Tracker line. */
  public readonly hasOeeLine: Signal<boolean> = computed(() => this.store.oeeLineId() !== null);
  public readonly userSource: Signal<ECheckInUserSource> = computed(() =>
    this.hasOeeLine() ? this.selectedUserSource() : ECheckInUserSource.boardMembers,
  );
  private readonly userSourceChanges$ = toObservable(this.userSource).pipe(distinctUntilChanged(), skip(1));
  /**
   * The user picker waits for both lists: showing board members first and switching to the line's users once they
   * arrive would move the cards under the user's cursor.
   */
  public readonly isUserPickerReady: Signal<boolean> = computed(
    () => this.store.isBoardMembersSettled() && (!this.data.itemId || this.store.isOeeLineUsersSettled()),
  );

  private readonly selectedAssignmentId: Signal<number | undefined> = toSignal(
    this.workInstructionControl.valueChanges.pipe(
      startWith(this.workInstructionControl.value),
      map((value: IDropdownOption[] | null) => value?.[0]?.id),
    ),
    { initialValue: undefined },
  );
  private readonly selectedStepSubmissionId: Signal<number | undefined> = toSignal(
    this.stepControl.valueChanges.pipe(
      startWith(this.stepControl.value),
      map((value: IDropdownOption[] | null) => value?.[0]?.id),
    ),
    { initialValue: undefined },
  );
  /** Started, not completed steps of the selected work instruction; the step picker is hidden when there is none. */
  public readonly stepOptions: Signal<IDropdownOption[]> = computed(() =>
    (this.stepsByAssignmentId.get(this.selectedAssignmentId() ?? -1) ?? []).map(
      (step: IWorkInstructionCheckInStep) => ({ id: step.guideSubmissionId, name: step.name }),
    ),
  );
  private readonly selectedDestination: Signal<ICheckInDestination | undefined> = computed(() => {
    const assignmentId: number | undefined = this.selectedAssignmentId();
    const stepSubmissionId: number | undefined = this.selectedStepSubmissionId();

    if (assignmentId === undefined) {
      return undefined;
    }

    return stepSubmissionId === undefined
      ? { objectId: assignmentId, typeId: ECheckInDestinationType.workInstruction }
      : { objectId: stepSubmissionId, typeId: ECheckInDestinationType.workInstructionStep };
  });
  private readonly now: Signal<number> = toSignal(interval(DURATION_REFRESH_MS).pipe(map(() => Date.now())), {
    initialValue: Date.now(),
  });

  /** Users already checked in to the selected destination can't be checked in to it again. */
  public readonly userOptions: Signal<IDropdownOption[]> = computed(() => {
    const selectedDestination: ICheckInDestination | undefined = this.selectedDestination();
    const checkedInUserIds = new Set<number>(
      this.store
        .checkIns()
        .filter(
          (checkIn: ICheckIn) =>
            checkIn.destinationTypeId === selectedDestination?.typeId &&
            checkIn.destinationObjectId === selectedDestination.objectId,
        )
        .map((checkIn: ICheckIn) => checkIn.sourceObjectId),
    );

    const users: IDropdownOption[] =
      this.userSource() === ECheckInUserSource.oeeLine ? this.store.oeeLineUsers() : this.store.boardMembers();

    return users.filter((user: IDropdownOption) => !checkedInUserIds.has(user.id));
  });
  public readonly userCards: Signal<ICheckInUserCard[]> = computed(() =>
    this.userOptions().map((user: IDropdownOption) => ({
      id: user.id,
      isSelected: user.id === this.selectedUserId(),
      name: user.name,
    })),
  );
  /** Ongoing check-ins as one flat list of cards, newest first; each card names its work instruction and step. */
  public readonly rows: Signal<IWorkInstructionCheckInRow[]> = computed(() => {
    const now: number = this.now();

    return orderBy(
      this.store.checkIns(),
      (checkIn: ICheckIn) => WorkInstructionCheckInComponent.getTimestamp(checkIn.checkInTime),
      'desc',
    ).map((checkIn: ICheckIn) =>
      this.toRow(checkIn, now - WorkInstructionCheckInComponent.getTimestamp(checkIn.checkInTime)),
    );
  });
  /** Checked-out check-ins, only while they are shown; latest check-out first. */
  public readonly checkedOutRows: Signal<IWorkInstructionCheckInRow[]> = computed(() =>
    this.isCheckedOutShown()
      ? orderBy(
          this.store.checkedOutCheckIns(),
          (checkIn: ICheckIn) => WorkInstructionCheckInComponent.getTimestamp(checkIn.checkOutTime),
          'desc',
        ).map((checkIn: ICheckIn) =>
          this.toRow(
            checkIn,
            WorkInstructionCheckInComponent.getTimestamp(checkIn.checkOutTime) -
              WorkInstructionCheckInComponent.getTimestamp(checkIn.checkInTime),
          ),
        )
      : [],
  );
  public readonly hasOngoingCheckIns: Signal<boolean> = computed(() => this.rows().length > 0);
  public readonly hasCheckedOutCheckIns: Signal<boolean> = computed(() => this.checkedOutRows().length > 0);

  private static getTimestamp(dateString: string | null): number {
    return dateString ? DateUtilitiesService.getDayJsFromString(dateString).valueOf() : 0;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: IWorkInstructionCheckInDialogData,
    public readonly store: WorkInstructionCheckInStore,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly destroyRef: DestroyRef,
  ) {}

  public ngOnInit(): void {
    this.store.loadCheckIns(
      this.data.workInstructions.map(
        (workInstruction: IWorkInstructionCheckInTarget) => workInstruction.itemGuideAssignmentId,
      ),
    );

    // A picked user belongs to the list it was picked from: switching lists clears it.
    this.userSourceChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.userControl.reset([]));
    // Steps belong to one work instruction: a new work instruction clears the step choice.
    this.workInstructionControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.stepControl.reset([]));
  }

  /** Reloaded on every show: check-outs made elsewhere since the last time are listed too. */
  public onToggleCheckedOutClick(): void {
    const isShown = !this.isCheckedOutShown();

    this.isCheckedOutShown.set(isShown);

    if (isShown) {
      this.store.loadCheckedOutCheckIns();
    }
  }

  public onUserSourceChange(source: ECheckInUserSource): void {
    this.selectedUserSource.set(source);
  }

  /** The form is hidden until asked for; its user lists are loaded the first time it opens. */
  public onNewCheckInClick(): void {
    if (!this.areUserListsRequested) {
      this.areUserListsRequested = true;
      this.store.loadBoardMembers({ boardId: this.data.boardId });

      if (this.data.itemId) {
        this.store.loadOeeLineUsers(this.data.itemId);
      }
    }

    this.isNewCheckInOpen.set(true);
  }

  public onNewCheckInCancel(): void {
    this.isNewCheckInOpen.set(false);
    this.isCheckInSubmitted = false;
    this.userControl.reset([]);
  }

  /** Single choice: clicking the picked card again clears it. */
  public onUserCardClick(user: ICheckInUserCard): void {
    this.userControl.setValue(user.isSelected ? [] : [{ id: user.id, name: user.name }]);
  }

  /** The button is never disabled: missing fields are reported on the dropdowns instead. */
  public onCheckInClick(): void {
    const selectedUser: IDropdownOption | undefined = this.userControl.value?.[0];
    const destination: ICheckInDestination | undefined = this.selectedDestination();

    if (!selectedUser || !destination) {
      this.isCheckInSubmitted = true;

      return;
    }

    // Ignore repeated clicks while the previous check-in / check-out request is running.
    if (this.store.isActionLoading()) {
      return;
    }

    this.store.checkIn({
      destination,
      successMessage: this.translate.instant('workInstruction.checkIn.checkedInMessage', {
        destination: this.getDestinationLabel(
          this.selectedAssignmentId() ?? -1,
          this.stepControl.value?.[0]?.name ?? null,
        ),
        source: selectedUser.name,
      }),
      userId: selectedUser.id,
    });
    this.isCheckInSubmitted = false;
    this.userControl.reset([]);
  }

  public onCheckOutClick(checkIn: ICheckIn): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          cancelButtonText: this.translate.instant('button.cancel'),
          content: this.getCheckOutConfirmationContent(checkIn),
          submitButtonText: this.translate.instant('button.checkOut'),
          title: this.translate.instant('workInstruction.checkIn.checkOutConfirmationTitle'),
        },
        width: EDialogWidth.xxSmall,
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() =>
        this.store.checkOut({
          checkInId: checkIn.id,
          successMessage: this.translate.instant('workInstruction.checkIn.checkedOutMessage', {
            destination: this.getDestinationLabel(checkIn.itemGuideAssignmentId, checkIn.guideStepName),
            source: checkIn.user?.name ?? '',
          }),
        }),
      );
  }

  /**
   * "<source> will be checked out from <destination>." + the confirmation question. Kept generic (source/destination)
   * because check-ins aren't tied to users and work instructions only. Names are escaped: the dialog renders HTML.
   */
  private getCheckOutConfirmationContent(checkIn: ICheckIn): string {
    const source: string = escape(checkIn.user?.name ?? '');
    const destination: string = escape(this.getDestinationLabel(checkIn.itemGuideAssignmentId, checkIn.guideStepName));

    return `${this.translate.instant('workInstruction.checkIn.checkOutConfirmationContent', {
      destination: `<strong>${destination}</strong>`,
      source: `<strong>${source}</strong>`,
    })}<br /><br />${this.translate.instant('workInstruction.checkIn.checkOutConfirmationQuestion')}`;
  }

  /** "<work instruction>", or "<work instruction> › Step: <step>" for a step. */
  private getDestinationLabel(itemGuideAssignmentId: number, stepName: string | null): string {
    const workInstructionName: string = this.workInstructionNames.get(itemGuideAssignmentId) ?? '';

    return stepName
      ? `${workInstructionName} › ${this.translate.instant('workInstruction.checkIn.stepLabel', { name: stepName })}`
      : workInstructionName;
  }

  /** Work instruction of the initial destination (a step's one when a step is given); else the only one. */
  private getInitialWorkInstructionOptions(): IDropdownOption[] {
    const { initialDestination } = this.data;
    const initialAssignmentId: number | undefined =
      initialDestination?.typeId === ECheckInDestinationType.workInstructionStep
        ? this.data.workInstructions.find((workInstruction: IWorkInstructionCheckInTarget) =>
            workInstruction.steps?.some(
              (step: IWorkInstructionCheckInStep) => step.guideSubmissionId === initialDestination.objectId,
            ),
          )?.itemGuideAssignmentId
        : initialDestination?.objectId;
    const initialOption: IDropdownOption | undefined = this.workInstructionOptions.find(
      (option: IDropdownOption) => option.id === initialAssignmentId,
    );

    if (initialOption) {
      return [initialOption];
    }

    return this.isMultiple ? [] : this.workInstructionOptions.slice(0, 1);
  }

  private getInitialStepOptions(): IDropdownOption[] {
    const { initialDestination } = this.data;

    if (initialDestination?.typeId !== ECheckInDestinationType.workInstructionStep) {
      return [];
    }

    const step: IWorkInstructionCheckInStep | undefined = this.data.workInstructions
      .flatMap((workInstruction: IWorkInstructionCheckInTarget) => workInstruction.steps ?? [])
      .find((candidate: IWorkInstructionCheckInStep) => candidate.guideSubmissionId === initialDestination.objectId);

    return step ? [{ id: step.guideSubmissionId, name: step.name }] : [];
  }

  private toRow(checkIn: ICheckIn, durationMs: number): IWorkInstructionCheckInRow {
    return {
      checkIn,
      durationLabel: this.formatDuration(durationMs / 1000, checkIn.checkOutTime === null),
      stepLabel: checkIn.guideStepName
        ? this.translate.instant('workInstruction.checkIn.stepLabel', { name: checkIn.guideStepName })
        : null,
      workInstructionName: this.workInstructionNames.get(checkIn.itemGuideAssignmentId) ?? '',
    };
  }

  /**
   * Days / hours / minutes, leading zero units dropped: "1d 4h 26m", "4h 26m". An ongoing check-in under a minute old
   * reads "Just now"; a checked-out one under a minute "0m".
   */
  private formatDuration(totalSeconds: number, isOngoing: boolean): string {
    const seconds: number = Math.max(0, Math.floor(totalSeconds));

    if (isOngoing && seconds < SECONDS_IN_MINUTE) {
      return this.translate.instant('workInstruction.checkIn.justNow');
    }

    const days: number = Math.floor(seconds / SECONDS_IN_DAY);
    const hours: number = Math.floor((seconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR);
    const minutes: number = Math.floor((seconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);
    const parts: string[] = [];

    if (days) {
      parts.push(this.translate.instant('workInstruction.checkIn.durationDays', { value: days }));
    }

    if (days || hours) {
      parts.push(this.translate.instant('workInstruction.checkIn.durationHours', { value: hours }));
    }

    parts.push(this.translate.instant('workInstruction.checkIn.durationMinutes', { value: minutes }));

    return parts.join(' ');
  }
}
